/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { AwvInfos } from "client";
import {
  useContext,
  createContext,
  Dispatch,
  SetStateAction,
  PropsWithChildren,
  FC,
  useState,
  useCallback,
} from "react";

interface AwvDataContext {
  awvData: AwvInfos;
  setAwvData: Dispatch<SetStateAction<AwvInfos>>;
  reset: () => void;
}

const defaultValue: AwvDataContext = {
  awvData: {
    awvInfos: [],
  },
  setAwvData: (value) => {
    // default value's function is not called.
  },
  reset: () => {
    // default value's function is not called.
  },
};

const context = createContext<AwvDataContext>(defaultValue);

export const AwvDataProvider: FC<PropsWithChildren> = ({ children }) => {
  const [awvData, setAwvData] = useState<AwvInfos>(defaultValue.awvData);
  const reset = useCallback(() => {
    setAwvData(defaultValue.awvData);
  }, []);

  return (
    <context.Provider
      value={{
        awvData,
        setAwvData,
        reset,
      }}
    >
      {children}
    </context.Provider>
  );
};

export const useAwvDataContext = () => {
  return useContext(context);
};
